import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { doIfLoadingRequired, StateResource } from '@schir-int-client/ngrx-helpers';
import { VerfahrenFacade } from '@schir-int-client/verfahren-shared';
import { isEmpty, isNil } from 'lodash-es';
import { Observable, of } from 'rxjs';
import { filter, mergeMap } from 'rxjs/operators';
import {
	createAlternativeTextEntryAction,
	deleteAlternativeTextEntryAction,
	loadAlternativeTexteAction,
	updateAlternativeTextEntryAction,
} from './alternative-text.actions';
import {
	AlternativeText,
	AlternativeTextResource,
	CreateAlternativeText,
	FlaggenrechtWithAlternativeText,
} from './alternative-text.model';
import { AlternativeTextState } from './alternative-text.reducer';
import {
	alternativeTextByIdSelector,
	alternativeTextSelectors,
	isAlternativeTexteSavedSelector,
} from './alternative-text.selectors';
import { FlaggenrechtChangeEntryResource } from '@schir-int-client/register-change';

@Injectable({ providedIn: 'root' })
export class AlternativeTextFacade {
	constructor(
		private store: Store<AlternativeTextState>,
		private verfahrenFacade: VerfahrenFacade
	) {}

	getAlternativeTextResources(): Observable<StateResource<AlternativeTextResource[]>> {
		return this.store
			.select(alternativeTextSelectors)
			.pipe(
				filter(
					alternativeTexte =>
						!doIfLoadingRequired(alternativeTexte, () => this.store.dispatch(loadAlternativeTexteAction()))
				)
			);
	}

	// Careful: Side effects only take effect if observable is subscribed.
	erstellen(entries: FlaggenrechtWithAlternativeText[]): Observable<StateResource<boolean>> {
		entries.forEach(entry =>
			isNil(entry.alternativeTextResource)
				? this.proceedWithNewEntry(entry)
				: this.proceedWithExistingEntry(entry)
		);

		return this.isAlternativeTextSaved().pipe(
			mergeMap(saveState => (saveState.resource ? this.downloadAmtlicherAuszug() : of(saveState)))
		);
	}

	private downloadAmtlicherAuszug(): Observable<StateResource<boolean>> {
		return this.verfahrenFacade.downloadAmtlicherAuszug();
	}

	private proceedWithNewEntry(entry: FlaggenrechtWithAlternativeText): void {
		if (!isEmpty(entry.value) || !entry.containOriginalText) {
			const alternativeTextToCreate: CreateAlternativeText = {
				value: entry.value,
				containOriginalText: entry.containOriginalText,
			};
			this.createAlternativeTextEntry(entry.flaggenrechtResource, alternativeTextToCreate);
		}
	}

	private proceedWithExistingEntry(entry: FlaggenrechtWithAlternativeText): void {
		if (isEmpty(entry.value) && entry.containOriginalText) {
			this.deleteAlternativeTextEntry(entry.alternativeTextResource);
		} else {
			const alternativeResource: AlternativeTextResource = entry.alternativeTextResource;
			const alternativeTextToUpdate: AlternativeText = {
				value: entry.value,
				containOriginalText: entry.containOriginalText,
				spaltenEintragId: alternativeResource.spaltenEintragId,
			};
			this.updateAlternativeTextEntry(alternativeResource, alternativeTextToUpdate);
		}
	}

	createAlternativeTextEntry(
		flaggenrechtResource: FlaggenrechtChangeEntryResource,
		alternativeText: CreateAlternativeText
	): void {
		this.store.dispatch(createAlternativeTextEntryAction({ flaggenrechtResource, alternativeText }));
	}

	updateAlternativeTextEntry(
		alternativeTextResource: AlternativeTextResource,
		alternativeText: AlternativeText
	): void {
		this.store.dispatch(updateAlternativeTextEntryAction({ alternativeTextResource, alternativeText }));
	}

	deleteAlternativeTextEntry(alternativeTextResource: AlternativeTextResource): void {
		this.store.dispatch(deleteAlternativeTextEntryAction({ alternativeTextResource }));
	}

	getAlternativeTextById(changeEntryId: string): Observable<AlternativeTextResource> {
		return this.store.select(alternativeTextByIdSelector(changeEntryId));
	}

	isAlternativeTextSaved(): Observable<StateResource<boolean>> {
		return this.store.select(isAlternativeTexteSavedSelector);
	}
}
